import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeftIcon } from 'lucide-react';
import { bridge } from '@/lib/bridge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

type PageShellProps = {
  title: string;
  description?: string;
  backTo?: string;
  /**
   * 占满视口：容器不滚动，由页面自己分「顶部固定 / 中间滚动 / 底部固定」三段。
   * 用它的页面要把头部和底部包在 `shrink-0` 里，中间放 `min-h-0 flex-1 overflow-y-auto`。
   */
  fill?: boolean;
  children: ReactNode;
};

/**
 * 页面外壳。
 *
 * **在 App 内不渲染自己的标题/返回** —— 标题由原生 `BridgeWebView` 的导航栏负责
 * （照搬搬小书：它的 `/mobile/*` 页都没有页内标题）。页内标题只在浏览器里
 * （`?mock=1` 调试、或直接访问）才显示。
 *
 * 底部留白按是否在 App 内区分：App 内要避开原生 tab bar / 手势条。
 */
export function PageShell({ title, description, backTo, fill, children }: PageShellProps) {
  const native = bridge.isNative;
  return (
    <div
      className={cn(
        'mx-auto flex w-full max-w-2xl flex-col gap-4 px-4',
        native ? 'pt-4 pb-28' : 'py-6 pb-16',
        fill && 'h-[100dvh] overflow-hidden',
      )}
    >
      {!native ? (
        <header className="flex flex-col gap-3">
          {backTo ? (
            <Button asChild variant="ghost" size="sm" className="-ml-2 w-fit">
              <Link to={backTo}>
                <ChevronLeftIcon data-icon="inline-start" />
                返回
              </Link>
            </Button>
          ) : null}
          <div className="flex flex-col gap-1">
            <h1 className="text-title3 font-semibold tracking-tight">{title}</h1>
            {description ? (
              <p className="text-muted-foreground text-subheadline">{description}</p>
            ) : null}
          </div>
        </header>
      ) : null}
      {children}
    </div>
  );
}
